import { useState, useEffect } from "react";
import useCreate from "../../hooks/useCreate";
import useRead from "../../hooks/useRead";
import toast from "react-hot-toast";

function useBlogForm({ blog_id, isEditMode = false, onClose }) {
  // Initial state for the blog form
  const initialState = {
    slug: "",
    title: "",
    description: "",
    content: "",
    published_at: new Date(),
    document_id: "",
    image_alt: "",
    thumbnail: "", // Add thumbnail field
    meta_title: "",
    meta_description: "",
    seo: {
      title: "",
      description: "",
    },
    status: "1",
  };

  const [blogFormData, setBlogFormData] = useState(initialState);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Fetch blog data if in edit mode
  const { data } = useRead({
    url: "get-blog-data",
    method: "GET",
    initialData: blog_id ? { blog_id } : null,
    enabled: isEditMode && !!blog_id,
    onSuccess: (response) => {
      if (isEditMode && response?.Data) {
        const blogData = response.Data;

        // Parse dates
        let publishDate = new Date();
        if (blogData?.published_at) {
          const [datePart, timePart] = blogData.published_at.split(' ');
          publishDate = new Date(`${datePart}T${timePart}`);
        }

        // Parse SEO JSON if it's a string
        let seoData = { title: "", description: "" };
        if (blogData?.seo) {
          try {
            seoData = typeof blogData.seo === 'string'
              ? JSON.parse(blogData.seo)
              : blogData.seo;
          } catch (e) {
            console.error("Error parsing SEO JSON:", e);
          }
        }

        const imagePath = blogData?.blog_document?.[0]?.path || "";

        setBlogFormData({
          blog_id,
          slug: blogData?.slug || "",
          title: blogData?.title || "",
          description: blogData?.description || "",
          content: blogData?.content || "",
          published_at: publishDate,
          document_id: blogData?.document_id || "",
          image_alt: blogData?.image_alt || "",
          thumbnail: blogData?.thumbnail || "", // Set thumbnail if it exists
          meta_title: blogData?.meta_title || seoData.title || "",
          meta_description: blogData?.meta_description || seoData.description || "",
          seo: seoData,
          status: blogData?.status || "1",
          path: imagePath,
        });
      }
    },
    onError: () => {
      toast.error("Failed to load blog data", {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  const { setDataToServer } = useCreate({
    refreshUrl: "get-blog-list",
    url: isEditMode ? "update-blog" : "create-blog",
    onSuccess: () => {
      setIsSuccess(true);
      toast.success(isEditMode ? "Blog Updated Successfully" : "Blog Added Successfully", {
        position: "top-right",
        duration: 3000,
      });

      if (!isEditMode) {
        // For AddBlog, we delay closing by 1.5s
        const timeout = setTimeout(() => {
          onClose();
        }, 1500);
        return () => clearTimeout(timeout);
      } else {
        // For UpdateBlog, we close immediately
        onClose();
      }
    },
    onError: () => {
      setIsError(true);
      toast.error("Oops! Something went wrong", {
        position: "top-right",
        duration: 3000,
      });
    },
  });

  // Handle form field changes
  const handleBlogFormData = (e) => {
    const { name, value } = e.target;
    setBlogFormData({
      ...blogFormData,
      [name]: value,
    });
  };

  // Handle SEO fields changes
  const handleBlogSeo = (e) => {
    const value = e.target.value;
    const tempName = e.target.attributes["data-name"].value;

    if (tempName === "title") {
      setBlogFormData({
        ...blogFormData,
        meta_title: value,
        seo: {
          ...blogFormData.seo,
          title: value,
        }
      });
    } else if (tempName === "description") {
      setBlogFormData({
        ...blogFormData,
        meta_description: value,
        seo: {
          ...blogFormData.seo,
          description: value,
        }
      });
    }
  };

  // Handle image alt change
  const handleImageAlt = (e) => {
    setBlogFormData({
      ...blogFormData,
      image_alt: e.target.value
    });
  };

  // Handle rich text editor content change
  const handleChangeEditor = (e, value) => {
    setBlogFormData({
      ...blogFormData,
      content: value,
    });
  };

  // Handle date change
  const handleSetPublishDate = (value) => {
    setBlogFormData({
      ...blogFormData,
      published_at: value,
    });
  };

  // Handle status change for edit mode
  const handleBlogStatus = (e) => {
    setBlogFormData({
      ...blogFormData,
      status: e.target.checked ? "1" : "0",
    });
  };

  // Generate slug from title
  const generateSlug = () => {
    if (blogFormData.title) {
      const slug = blogFormData.title
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');

      setBlogFormData({
        ...blogFormData,
        slug: slug
      });
    }
  };

  // Form submission handler
  const handleSubmitBlogFormData = (e) => {
    e.preventDefault();

    // Format the date to ISO string as required by the API
    const formattedDate = blogFormData.published_at instanceof Date
      ? blogFormData.published_at.toISOString().split('T')[0] + ' ' +
      blogFormData.published_at.toTimeString().split(' ')[0]
      : blogFormData.published_at;

    const payload = {
      ...blogFormData,
      published_at: formattedDate,
      seo: JSON.stringify(blogFormData.seo)
    };

    if (isEditMode) {
      payload.blog_id = blog_id;
    }

    setDataToServer.mutate(payload);
  };

  // Clear form data and close
  const clearData = () => {
    setBlogFormData(initialState);
    onClose();
  };

  // Reset error/success states
  const closeErrorMessage = () => {
    setIsSuccess(false);
    setIsError(false);
  };

  // Update document_id and image path when an image is uploaded
  const updateImageData = (document_id, imagePath) => {
    setBlogFormData(prev => ({
      ...prev,
      document_id,
      path: imagePath,
    }));
  };

  // Update thumbnail path when a thumbnail is uploaded
  const updateThumbnailData = (thumbnailPath) => {
    setBlogFormData(prev => ({
      ...prev,
      thumbnail: thumbnailPath,
    }));
  };

  // Clear image data when image is removed
  const clearImageData = () => {
    setBlogFormData(prev => ({
      ...prev,
      document_id: "",
      path: "",
    }));
  };

  // Clear thumbnail data when thumbnail is removed
  const clearThumbnailData = () => {
    setBlogFormData(prev => ({
      ...prev,
      thumbnail: "",
    }));
  };

  return {
    blogFormData,
    isLoading: data?.isLoading,
    isSubmitting: setDataToServer.isLoading,
    isError,
    isSuccess,
    handleBlogFormData,
    handleBlogSeo,
    handleImageAlt,
    handleChangeEditor,
    handleSetPublishDate,
    handleBlogStatus,
    generateSlug,
    handleSubmitBlogFormData,
    clearData,
    closeErrorMessage,
    updateImageData,
    updateThumbnailData, // Add thumbnail update handler
    clearImageData,
    clearThumbnailData,
  };
}

export default useBlogForm;
